import { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';

interface CardData {
  name: string;
  title: string;
  email: string;
  phone: string;
  website: string;
  tagline: string;
}

interface AppleWalletCardProps {
  card: CardData;
}

// ─── Apple Wallet Pass Visual ─────────────────────────────────────────────────

export function AppleWalletPassVisual({ card }: { card: CardData; flipped?: boolean }) {
  const isHector = card.name.toLowerCase().includes('hector');
  const [nfcActive, setNfcActive] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const nfcTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setCardVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const triggerNfc = useCallback(() => {
    setNfcActive(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setNfcActive(true));
    });
  }, []);

  useEffect(() => {
    const initial = setTimeout(() => {
      triggerNfc();
      nfcTimerRef.current = setInterval(triggerNfc, 6000);
    }, 900);
    return () => {
      clearTimeout(initial);
      if (nfcTimerRef.current) clearInterval(nfcTimerRef.current);
    };
  }, [triggerNfc]);

  return (
    <>
      <style>{`
        @keyframes walletShimmer {
          0% { left: -70%; }
          55% { left: 110%; }
          100% { left: 110%; }
        }
        @keyframes walletBorderPulse {
          0%, 100% { border-color: rgba(201,169,110,0.14); }
          50% { border-color: rgba(201,169,110,0.32); }
        }
        @keyframes nfcRipple {
          0%   { transform: scale(1);   opacity: 0.75; }
          100% { transform: scale(3.2); opacity: 0; }
        }
      `}</style>

      {/* Outer wrapper — handles entrance animation, overflow visible so ripples can escape */}
      <div
        className="relative w-full select-none"
        style={{
          borderRadius: 20,
          aspectRatio: '375 / 240',
          opacity: cardVisible ? 1 : 0,
          transform: cardVisible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.98)',
          transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* NFC ripple rings — outside the clipped card so they expand freely */}
        {nfcActive && [0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute rounded-full pointer-events-none"
            style={{
              top: '10%',
              right: '34%',
              width: 28,
              height: 28,
              marginTop: 8,
              marginRight: 8,
              border: '1px solid rgba(201,169,110,0.7)',
              animation: `nfcRipple 1.4s cubic-bezier(0.2, 0.6, 0.4, 1) ${i * 0.22}s forwards`,
              zIndex: 20,
            }}
          />
        ))}

        {/* Card body — clipped to border radius, all card content lives here */}
        <div
          className="absolute inset-0"
          style={{
            borderRadius: 20,
            background: 'linear-gradient(145deg, #1C1C1E 0%, #2C2C2E 40%, #1A1A1C 100%)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          {/* Subtle noise texture overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
              borderRadius: 20,
              opacity: 0.6,
            }}
          />

          {/* Gold shimmer accent line at top */}
          <div className="absolute top-0 left-0 right-0 overflow-hidden" style={{ height: 2, borderRadius: '20px 20px 0 0' }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '70%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent 0%, rgba(201,169,110,0.6) 35%, rgba(255,220,140,0.95) 50%, rgba(201,169,110,0.6) 65%, transparent 100%)',
                animation: 'walletShimmer 4s ease-in-out infinite',
              }}
            />
          </div>

          {/* Subtle gold border */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              borderRadius: 20,
              border: '1px solid rgba(201,169,110,0.18)',
              animation: 'walletBorderPulse 5s ease-in-out infinite',
            }}
          />

          {/* Silhouette image — right side */}
          <div
            className="absolute right-0 top-0 bottom-0"
            style={{ width: '38%', overflow: 'hidden', borderRadius: '0 20px 20px 0' }}
          >
            <img
              src={
                isHector
                  ? 'https://public.readdy.ai/ai/img_res/b8164775ac4a5f594477f003ad6cce2f.jpg'
                  : 'https://readdy.ai/api/search-image?query=elegant%20professional%20silhouette%20in%20a%20tailored%20dark%20suit%20standing%20with%20poised%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%2C%20identity%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20presence%2C%20dark%20background%20blending%20seamlessly&width=240&height=320&seq=generic-silhouette-card-01&orientation=portrait'
              }
              alt=""
              className="w-full h-full object-cover object-top"
              style={{ opacity: 0.5, mixBlendMode: 'luminosity' }}
            />
            {/* Fade to left */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to right, #1C1C1E 0%, rgba(28,28,30,0.3) 55%, transparent 100%)' }}
            />
          </div>

          {/* Corner accent (top-right) */}
          <div
            className="absolute top-0 right-0"
            style={{
              width: 70,
              height: 70,
              borderBottom: '1px solid rgba(201,169,110,0.2)',
              borderLeft: '1px solid rgba(201,169,110,0.2)',
              borderBottomLeftRadius: 50,
            }}
          />

          {/* Pass content — full width, flex layout */}
          <div className="absolute inset-0 flex flex-col justify-between" style={{ padding: '18px 20px 44px 20px' }}>

            {/* Top row: HSG logo + NFC chip */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span
                  style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontSize: 18,
                    fontWeight: 600,
                    letterSpacing: '0.18em',
                    color: '#C9A96E',
                    lineHeight: 1,
                  }}
                >
                  HSG
                </span>
                <div style={{ width: 1, height: 18, backgroundColor: 'rgba(201,169,110,0.3)' }} />
                <div className="flex flex-col" style={{ gap: 2 }}>
                  <span
                    style={{
                      fontFamily: 'Inter, -apple-system, sans-serif',
                      fontSize: 6,
                      fontWeight: 500,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: '#C9A96E',
                      lineHeight: 1,
                    }}
                  >
                    Higher Standard
                  </span>
                  <span
                    style={{
                      fontFamily: 'Inter, -apple-system, sans-serif',
                      fontSize: 6,
                      fontWeight: 300,
                      letterSpacing: '0.35em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.5)',
                      lineHeight: 1,
                    }}
                  >
                    Global
                  </span>
                </div>
              </div>

              {/* NFC chip body */}
              <div className="relative flex items-center justify-center" style={{ width: 44, height: 44 }}>
                <div
                  className="relative z-10 flex items-center justify-center"
                  style={{
                    width: 28,
                    height: 20,
                    borderRadius: 4,
                    border: '1px solid rgba(201,169,110,0.3)',
                    background: 'rgba(201,169,110,0.06)',
                    transition: 'border-color 0.3s, background 0.3s',
                    ...(nfcActive ? {
                      borderColor: 'rgba(201,169,110,0.6)',
                      background: 'rgba(201,169,110,0.12)',
                    } : {}),
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        style={{
                          width: 10 - i * 2,
                          height: 1,
                          borderRadius: 1,
                          backgroundColor: `rgba(201,169,110,${nfcActive ? 0.9 - i * 0.15 : 0.6 - i * 0.15})`,
                          transition: 'background-color 0.3s',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Middle: Pass type label */}
            <div>
              <p
                style={{
                  fontFamily: 'Inter, -apple-system, sans-serif',
                  fontSize: 7,
                  fontWeight: 500,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: 'rgba(201,169,110,0.55)',
                  margin: 0,
                  marginBottom: 2,
                }}
              >
                Business Card
              </p>
            </div>

            {/* Bottom: Name + title + contact */}
            <div style={{ width: '62%' }}>
              <div
                style={{
                  height: 1,
                  background: 'linear-gradient(to right, rgba(201,169,110,0.45) 0%, rgba(201,169,110,0) 100%)',
                  marginBottom: 10,
                }}
              />
              <p
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: 20,
                  fontWeight: 500,
                  color: '#FFFFFF',
                  letterSpacing: '0.03em',
                  lineHeight: 1.15,
                  margin: 0,
                  marginBottom: 3,
                }}
              >
                {card.name || 'Your Name'}
              </p>
              <p
                style={{
                  fontFamily: 'Inter, -apple-system, sans-serif',
                  fontSize: 7,
                  fontWeight: 400,
                  color: '#C9A96E',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  margin: 0,
                  marginBottom: 10,
                }}
              >
                {card.title}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {card.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 10, height: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="ri-mail-line" style={{ fontSize: 8, color: 'rgba(201,169,110,0.6)' }}></i>
                    </div>
                    <p style={{ fontFamily: 'Inter, -apple-system, sans-serif', fontSize: 7, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.02em', margin: 0 }}>{card.email}</p>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                  {card.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 10, height: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="ri-phone-line" style={{ fontSize: 8, color: 'rgba(201,169,110,0.6)' }}></i>
                      </div>
                      <p style={{ fontFamily: 'Inter, -apple-system, sans-serif', fontSize: 7, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.02em', margin: 0 }}>{card.phone}</p>
                    </div>
                  )}
                  {card.website && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 10, height: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="ri-global-line" style={{ fontSize: 8, color: 'rgba(201,169,110,0.6)' }}></i>
                      </div>
                      <p style={{ fontFamily: 'Inter, -apple-system, sans-serif', fontSize: 7, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.02em', margin: 0 }}>{card.website}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom strip: tagline + Apple Wallet mark */}
          <div
            className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5"
            style={{
              height: 28,
              background: 'rgba(0,0,0,0.25)',
              borderTop: '1px solid rgba(201,169,110,0.1)',
            }}
          >
            <p
              style={{
                fontFamily: 'Inter, -apple-system, sans-serif',
                fontSize: 7,
                fontWeight: 300,
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                color: 'rgba(201,169,110,0.4)',
                margin: 0,
              }}
            >
              {card.tagline || 'By Reference Only'}
            </p>
            <div className="flex items-center gap-1" style={{ opacity: 0.35 }}>
              <i className="ri-apple-line" style={{ fontSize: 9, color: '#FFFFFF' }}></i>
              <span style={{ fontFamily: '-apple-system, sans-serif', fontSize: 7, color: '#FFFFFF', letterSpacing: '0.05em' }}>Wallet</span>
            </div>
          </div>

        </div>{/* end card body */}
      </div>{/* end outer wrapper */}
    </>
  );
}

// ─── iOS Wallet Phone Mockup ──────────────────────────────────────────────────

function WalletPhoneMockup({ card }: { card: CardData }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative"
        style={{
          width: 260,
          background: '#1C1C1E',
          borderRadius: 40,
          padding: '12px 8px 16px',
          boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 32px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.1)',
        }}
      >
        {/* Status bar */}
        <div className="flex items-center justify-between px-4 mb-2" style={{ height: 20 }}>
          <span style={{ fontFamily: '-apple-system, sans-serif', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.85)', letterSpacing: '-0.02em' }}>9:41</span>
          <div
            style={{
              width: 80,
              height: 20,
              background: '#1C1C1E',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.08)',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: 12,
            }}
          />
          <div className="flex items-center gap-1">
            <i className="ri-signal-wifi-fill" style={{ fontSize: 9, color: 'rgba(255,255,255,0.85)' }}></i>
            <i className="ri-battery-fill" style={{ fontSize: 10, color: 'rgba(255,255,255,0.85)' }}></i>
          </div>
        </div>

        {/* Wallet header */}
        <div className="flex items-center justify-between px-3 mb-3">
          <span style={{ fontFamily: '-apple-system, sans-serif', fontSize: 13, fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.02em' }}>Wallet</span>
          <div
            className="flex items-center justify-center"
            style={{ width: 24, height: 24, borderRadius: 12, background: 'rgba(255,255,255,0.1)' }}
          >
            <i className="ri-add-line" style={{ fontSize: 12, color: '#FFFFFF' }}></i>
          </div>
        </div>

        {/* Pass card in wallet */}
        <div className="px-2">
          <AppleWalletPassVisual card={card} />
        </div>

        {/* Wallet bottom actions */}
        <div className="flex items-center justify-center gap-6 mt-4 px-4">
          {[
            { icon: 'ri-share-line', label: 'Share' },
            { icon: 'ri-information-line', label: 'Info' },
            { icon: 'ri-delete-bin-line', label: 'Remove' },
          ].map((action) => (
            <div key={action.label} className="flex flex-col items-center gap-1">
              <div
                className="flex items-center justify-center"
                style={{ width: 32, height: 32, borderRadius: 16, background: 'rgba(255,255,255,0.1)' }}
              >
                <i className={action.icon} style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}></i>
              </div>
              <span style={{ fontFamily: '-apple-system, sans-serif', fontSize: 8, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.02em' }}>{action.label}</span>
            </div>
          ))}
        </div>

        {/* Home indicator */}
        <div
          className="mx-auto mt-3"
          style={{ width: 80, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.25)' }}
        />
      </div>

      <p className="text-[10px] text-[#9A9A9A] text-center">How it looks in Apple Wallet</p>
    </div>
  );
}

// ─── pkpass Coming Soon Teaser ────────────────────────────────────────────────

function PkpassComingSoon() {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden border border-[#C9A96E]/20">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#1A1A1A] to-[#2C2C2C] hover:from-[#2C2C2C] hover:to-[#3A3A3A] transition-all cursor-pointer text-left"
      >
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <i className="ri-apple-line text-sm text-[#C9A96E]"></i>
        </div>
        <div className="flex-1">
          <p className="text-xs font-medium text-white">Real .pkpass — Coming Soon</p>
          <p className="text-[10px] text-[#C9A96E]/60 mt-0.5">Native Apple Wallet pass with signing</p>
        </div>
        <span className="text-[9px] font-medium text-[#C9A96E] bg-[#C9A96E]/15 px-2 py-0.5 rounded-full border border-[#C9A96E]/30 whitespace-nowrap">
          Phase 2
        </span>
        {expanded
          ? <i className="ri-arrow-up-s-line text-sm text-[#C9A96E]/60 flex-shrink-0"></i>
          : <i className="ri-arrow-down-s-line text-sm text-[#C9A96E]/60 flex-shrink-0"></i>
        }
      </button>
      {expanded && (
        <div className="px-4 py-4 bg-[#1A1A1A]/5 border-t border-[#C9A96E]/10 space-y-3">
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            A native <strong className="text-[#1A1A1A]">.pkpass</strong> file opens directly in Apple Wallet — no contacts step needed. It requires an <strong className="text-[#1A1A1A]">Apple Developer Pass Type ID</strong> and certificate for cryptographic signing.
          </p>
          <div className="space-y-2">
            {[
              { icon: 'ri-shield-check-line', text: 'Cryptographically signed by Apple Developer cert' },
              { icon: 'ri-wallet-3-line', text: 'Opens natively in Apple Wallet — one tap to add' },
              { icon: 'ri-refresh-line', text: 'Updateable — push updates to all saved passes' },
              { icon: 'ri-notification-3-line', text: 'Lock screen notifications when contact is nearby' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-xs text-[#C9A96E]`}></i>
                </div>
                <p className="text-[11px] text-[#4B4B4B]">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#8B7355] leading-relaxed font-medium border-t border-[#C9A96E]/15 pt-3">
            To enable: provide your Apple Developer Pass Type ID + certificate. We&apos;ll build the full signing pipeline as a backend function.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Apple Wallet Tab ─────────────────────────────────────────────────────────

export default function AppleWalletTab({ card }: AppleWalletCardProps) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [shareStatus, setShareStatus] = useState<'idle' | 'sharing' | 'done'>('idle');
  const [copiedLink, setCopiedLink] = useState(false);
  const [activeView, setActiveView] = useState<'mockup' | 'card'>('mockup');
  const [photoB64, setPhotoB64] = useState('');
  const [photoMime, setPhotoMime] = useState('');
  const [photoLoading, setPhotoLoading] = useState(true);

  const cardUrl = 'https://higherstandardglobal.com/card/hector';

  // Load silhouette photo for embedding in vCard
  useEffect(() => {
    setPhotoLoading(true);
    const isHector = card.name.toLowerCase().includes('hector');
    const imgUrl = isHector
      ? 'https://readdy.ai/api/search-image?query=elegant%20male%20silhouette%20in%20a%20perfectly%20tailored%20dark%20charcoal%20suit%20standing%20with%20confident%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%20tracing%20the%20shoulder%20and%20jaw%20line%2C%20identity%20completely%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20and%20powerful%20presence%2C%20dark%20background%20blending%20seamlessly&width=480&height=640&seq=hector-silhouette-card-01&orientation=portrait'
      : 'https://readdy.ai/api/search-image?query=elegant%20professional%20silhouette%20in%20a%20tailored%20dark%20suit%20standing%20with%20poised%20posture%20against%20a%20deep%20black%20background%2C%20dramatic%20side%20lighting%20with%20warm%20amber%20gold%20rim%20light%2C%20identity%20obscured%20in%20shadow%2C%20luxury%20editorial%20photography%2C%20cinematic%20noir%20atmosphere%2C%20high%20contrast%2C%20anonymous%20executive%20portrait%2C%20sophisticated%20presence%2C%20dark%20background%20blending%20seamlessly&width=480&height=640&seq=generic-silhouette-card-01&orientation=portrait';

    fetch(imgUrl)
      .then(r => r.blob())
      .then(blob => {
        const mime = blob.type || 'image/jpeg';
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          setPhotoB64(result.split(',')[1] ?? '');
          setPhotoMime(mime);
          setPhotoLoading(false);
        };
        reader.onerror = () => setPhotoLoading(false);
        reader.readAsDataURL(blob);
      })
      .catch(() => setPhotoLoading(false));
  }, [card.name]);

  // Build vCard with embedded photo
  const buildVCard = useCallback(() => {
    const nameParts = card.name.trim().split(' ');
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0] || '';

    const lines: string[] = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `N:${lastName};${firstName};;;`,
      `FN:${card.name}`,
      card.title ? `TITLE:${card.title}` : '',
      'ORG:Higher Standard Global',
      card.email ? `EMAIL;TYPE=WORK,INTERNET:${card.email}` : '',
      card.phone ? `TEL;TYPE=WORK,VOICE:${card.phone}` : '',
      card.website ? `URL:https://${card.website.replace(/^https?:\/\//, '')}` : '',
      `NOTE:${card.tagline || 'By Reference Only'} — Higher Standard Global`,
      `URL;TYPE=WALLET:${cardUrl}`,
    ].filter(Boolean) as string[];

    if (photoB64 && photoMime) {
      const typeStr = photoMime.split('/')[1]?.toUpperCase() ?? 'JPEG';
      const header = `PHOTO;ENCODING=b;TYPE=${typeStr}:`;
      const chunkSize = 75 - header.length;
      const firstChunk = photoB64.slice(0, chunkSize);
      const rest = photoB64.slice(chunkSize);
      lines.push(header + firstChunk);
      for (let i = 0; i < rest.length; i += 74) {
        lines.push(' ' + rest.slice(i, i + 74));
      }
    }

    lines.push('END:VCARD');
    return lines.join('\r\n');
  }, [card, cardUrl, photoB64, photoMime]);

  useEffect(() => {
    QRCode.toDataURL(cardUrl, {
      width: 200,
      margin: 1,
      color: { dark: '#1A1A1A', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    }).then(setQrDataUrl).catch(() => {});
  }, [cardUrl]);

  // Primary CTA: share the vCard file
  const handleSendToWallet = async () => {
    setShareStatus('sharing');
    try {
      const vcard = buildVCard();
      const blob = new Blob([vcard], { type: 'text/vcard' });
      const safeName = card.name.replace(/\s+/g, '_');
      const file = new File([blob], `${safeName}_HSG.vcf`, { type: 'text/vcard' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${card.name} — Higher Standard Global`,
          text: `${card.name}, ${card.title} at Higher Standard Global`,
          files: [file],
        });
      } else if (navigator.share) {
        await navigator.share({
          title: `${card.name} — Higher Standard Global`,
          text: `${card.name}, ${card.title} at Higher Standard Global\n${card.email}\n${card.phone}\n${cardUrl}`,
          url: cardUrl,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safeName}_HSG.vcf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      setShareStatus('done');
      setTimeout(() => setShareStatus('idle'), 3000);
    } catch {
      setShareStatus('idle');
    }
  };

  const handleDownloadVCF = () => {
    const vcard = buildVCard();
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = card.name.replace(/\s+/g, '_');
    a.href = url;
    a.download = `${safeName}_HSG.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch { /* ignore */ }
  };

  const handleAirDrop = async () => {
    const vcard = buildVCard();
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const safeName = card.name.replace(/\s+/g, '_');
    const file = new File([blob], `${safeName}_HSG.vcf`, { type: 'text/vcard' });
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ title: `${card.name} — HSG`, files: [file] }); } catch { /* cancelled */ }
    } else if (navigator.share) {
      try { await navigator.share({ title: `${card.name} — HSG`, url: cardUrl }); } catch { /* cancelled */ }
    }
  };

  const handleIMessage = () => {
    const msg = `Hey! Here\u2019s my digital business card \u2014 ${card.name}, ${card.title} at Higher Standard Global. Save my contact in one tap: ${cardUrl}`;
    const encoded = encodeURIComponent(msg);
    window.open(`sms:&body=${encoded}`, '_blank');
  };

  return (
    <div className="flex flex-col gap-5">

      {/* View toggle */}
      <div className="flex items-center gap-1 bg-[#F7F4EF] rounded-full p-1 self-center">
        {[
          { id: 'mockup' as const, label: 'In Wallet' },
          { id: 'card' as const, label: 'Card Only' },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeView === v.id ? 'bg-[#1A1A1A] text-white' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Card visual */}
      <div className="flex justify-center">
        {activeView === 'mockup' ? (
          <WalletPhoneMockup card={card} />
        ) : (
          <div className="w-full">
            <AppleWalletPassVisual card={card} />
          </div>
        )}
      </div>

      {/* Primary CTA — Apple Wallet style button */}
      <div className="space-y-3">
        {/* Main add-to-wallet button */}
        <button
          onClick={handleSendToWallet}
          disabled={shareStatus === 'sharing' || photoLoading}
          className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 relative overflow-hidden"
          style={{
            background: shareStatus === 'done'
              ? 'linear-gradient(135deg, #4A7C59, #3D6B4A)'
              : 'linear-gradient(135deg, #1C1C1E 0%, #2C2C2E 100%)',
            color: '#FFFFFF',
            border: '1px solid rgba(201,169,110,0.25)',
          }}
        >
          {/* Gold shimmer on idle */}
          {shareStatus === 'idle' && !photoLoading && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(201,169,110,0.08) 50%, transparent 60%)',
                animation: 'walletShimmer 3s ease-in-out infinite',
              }}
            />
          )}
          {shareStatus === 'sharing' ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Opening Share Sheet…</span>
            </>
          ) : shareStatus === 'done' ? (
            <>
              <i className="ri-check-line text-lg"></i>
              <span>Sent to Wallet!</span>
            </>
          ) : photoLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Preparing card…</span>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <i className="ri-apple-line text-lg"></i>
                <div className="flex flex-col items-start leading-none">
                  <span style={{ fontSize: 9, letterSpacing: '0.08em', opacity: 0.7, textTransform: 'uppercase' }}>Add to</span>
                  <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>Apple Wallet</span>
                </div>
              </div>
            </>
          )}
        </button>

        {/* How it works — compact */}
        <div className="flex items-start gap-2.5 bg-[#F7F4EF] rounded-xl px-3.5 py-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-xs text-[#C9A96E]"></i>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-medium text-[#1A1A1A]">How it works on iPhone</p>
            <p className="text-[10px] text-[#6B6B6B] leading-relaxed">
              Tap above → iOS share sheet opens → tap <strong className="text-[#1A1A1A]">&ldquo;Add to Contacts&rdquo;</strong> → the contact with branded photo saves and appears in Apple Wallet automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Pass details */}
      <div className="bg-[#F7F4EF] rounded-xl p-4 space-y-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] mb-2">Pass Details</p>
        {[
          { icon: 'ri-user-line', label: 'Name', value: card.name },
          { icon: 'ri-briefcase-line', label: 'Title', value: card.title },
          { icon: 'ri-building-line', label: 'Company', value: 'Higher Standard Global' },
          { icon: 'ri-mail-line', label: 'Email', value: card.email },
          { icon: 'ri-phone-line', label: 'Phone', value: card.phone },
          { icon: 'ri-global-line', label: 'Web', value: card.website },
        ].filter(r => r.value).map((row, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center text-[#C9A96E] flex-shrink-0">
              <i className={`${row.icon} text-xs`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] text-[#9A9A9A] uppercase tracking-wide">{row.label}</p>
              <p className="text-xs text-[#1A1A1A] truncate">{row.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Secondary share options */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] mb-2.5">More ways to share</p>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={handleIMessage}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #34C759 0%, #28A745 100%)' }}
          >
            <i className="ri-message-2-fill text-sm"></i>
            iMessage
          </button>
          <button
            onClick={handleAirDrop}
            className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-[#E8E6E3] bg-white text-xs font-medium text-[#1A1A1A] hover:border-[#1A1A1A] hover:bg-[#F7F4EF] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-airplay-line text-sm text-[#C9A96E]"></i>
            AirDrop
          </button>
          <button
            onClick={handleCopyLink}
            className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${copiedLink ? 'bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30' : 'border-[#E8E6E3] bg-white text-[#1A1A1A] hover:border-[#1A1A1A] hover:bg-[#F7F4EF]'}`}
          >
            <i className={`${copiedLink ? 'ri-check-line' : 'ri-link'} text-sm ${copiedLink ? '' : 'text-[#C9A96E]'}`}></i>
            {copiedLink ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Download .vcf directly */}
      <button
        onClick={handleDownloadVCF}
        disabled={photoLoading}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#C9A96E]/40 text-[#8B7355] text-xs font-medium hover:bg-[#C9A96E]/10 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
      >
        {photoLoading
          ? <><div className="w-3 h-3 border border-[#C9A96E]/40 border-t-[#C9A96E] rounded-full animate-spin" />Embedding photo…</>
          : <><i className="ri-download-line text-sm"></i>Download .vcf Contact File</>
        }
      </button>

      {/* QR Code for scanning */}
      {qrDataUrl && (
        <div className="flex flex-col items-center gap-3 pt-1">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] self-start">Scan to open on iPhone</p>
          <div className="flex items-center gap-4 w-full bg-[#F7F4EF] rounded-xl p-4">
            <div className="bg-white rounded-lg p-2 flex-shrink-0" style={{ border: '1px solid #E8E6E3' }}>
              <img src={qrDataUrl} alt="QR code" className="w-[80px] h-[80px]" />
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-medium text-[#1A1A1A]">Point iPhone camera here</p>
              <p className="text-[10px] text-[#6B6B6B] leading-relaxed">Opens the digital card page — tap &ldquo;Save Contact&rdquo; to add to Contacts &amp; Wallet.</p>
              <p className="text-[9px] text-[#C9A96E] font-mono truncate">{cardUrl}</p>
            </div>
          </div>
        </div>
      )}

      {/* Coming Soon — real pkpass */}
      <PkpassComingSoon />

    </div>
  );
}
